import type {MaterialNodeDefinition} from "#material/nodes/base/material/types/definition"
import {ExchangeNodeName} from "#material/exchange/node-name"
import {materialNormalOutput} from "#material/nodes/base/material/validation/io"
import {materialUiSchema} from "#material/nodes/base/material/validation/ui"
import type {MaterialNodeSockets} from "#material/types/material-node-sockets"
import {vec3} from "#material/types/vector"
import {z} from "zod/v4-mini"
import {axisSchema, directionTypeSchema} from "#material/nodes/input/tangent/types"
import type {Axis, DirectionType} from "#material/nodes/input/tangent/types"

export interface TangentSockets extends MaterialNodeSockets {
    inputs: {}
    settings: {
        tangent: "vector" // found in inputs: -; found in settings: vector,output; inferred from data: float3,null
        uvMap: "string" // found in inputs: -; found in settings: string; inferred from data: string
        uvMapIndex: "float" // found in inputs: -; found in settings: integer; inferred from data: float
    }
    settingsExtensions: {
        axis?: Axis // found in inputs: -; found in settings: string; inferred from data: string
        directionType?: DirectionType // found in inputs: -; found in settings: string; inferred from data: string
    }
    outputs: {
        tangent: "normal"
    }
}

export const tangentDefinition = {
    exchangeFormat: {
        nodeName: ExchangeNodeName.ShaderNodeTangent,
    },
    nodeClass: "Tangent",
    ui: {
        label: "Tangent",
        color: "#9e343e",
        items: [
            {name: "tangent", valueType: "normal", label: "Tangent", output: true},
            {
                name: "directionType",
                defaultValue: "RADIAL",
                label: "Direction",
                valueType: "string",
                options: [
                    {label: "Radial", value: "RADIAL"},
                    {label: "UV Map", value: "UV_MAP"},
                ],
                input: false,
                setting: true,
            },
            {
                name: "axis",
                defaultValue: "X",
                label: "Axis",
                valueType: "string",
                options: [
                    {label: "X", value: "X"},
                    {label: "Y", value: "Y"},
                    {label: "Z", value: "Z"},
                ],
                input: false,
                setting: true,
            },
        ],
        support: {
            cycles: true,
            three: true,
        },
    },
    validation: {
        returns: z.strictObject({tangent: materialNormalOutput}),
        parameters: z.strictObject({
            inputs: z.strictObject({}),
            settings: z.strictObject({
                axis: z.optional(axisSchema),
                directionType: z.optional(directionTypeSchema),
                tangent: z.optional(vec3),
                uvMap: z.optional(z.string()),
                uvMapIndex: z.optional(z.number()),
            }),
            ui: materialUiSchema,
        }),
    },
} satisfies MaterialNodeDefinition<TangentSockets>
